'use client'

import { CheckCircle, SpinnerGap, WarningCircle } from '@phosphor-icons/react'
import { useRef, useState } from 'react'

const fieldClassName = 'mt-2 min-h-12 w-full rounded-xl border border-[var(--portfolio-line)] bg-[var(--portfolio-surface)] px-4 py-3 text-[var(--portfolio-ink)] placeholder:text-[var(--portfolio-muted)] transition hover:border-[#8392A8] focus:border-[var(--portfolio-accent-text)] focus:outline-none focus:ring-3 focus:ring-[#00E59E]/25'

export default function ContactForm() {
  const honeypotRef = useRef<HTMLInputElement>(null)
  const formStartedAt = useRef(Date.now())
  const [formData, setFormData] = useState({ name: '', email: '', company: '', website: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          fax: honeypotRef.current?.value ?? '',
          formElapsedMs: Date.now() - formStartedAt.current,
        }),
      })

      if (!response.ok) throw new Error('Contact request failed')

      setSubmitStatus('success')
      setFormData({ name: '', email: '', company: '', website: '', message: '' })
      formStartedAt.current = Date.now()
    } catch (error) {
      console.error('Contact form submission failed:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact-form" className="scroll-mt-24 bg-[var(--portfolio-surface)] px-4 py-20 sm:px-6 sm:py-24" aria-labelledby="contact-title">
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[.72fr_1.28fr] lg:gap-20">
        <div>

          <h2 id="contact-title" className="max-w-[12ch] text-4xl font-normal leading-[1.05] tracking-[-0.04em] text-[var(--portfolio-ink)] sm:text-5xl">
            Send the useful details.
          </h2>
          <p className="mt-6 max-w-[38ch] leading-7 text-[var(--portfolio-muted)]">
            Tell me what is unreliable, missing, or hard to explain. I will reply within 24 hours with a useful next step.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-[var(--portfolio-line)] bg-[var(--portfolio-paper)] p-5 sm:p-8" aria-describedby="contact-response-time">
          <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
            <label htmlFor="fax">Fax number</label>
            <input ref={honeypotRef} type="text" id="fax" name="fax" tabIndex={-1} autoComplete="off" />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <label className="text-sm font-semibold text-[var(--portfolio-ink)]" htmlFor="name">
              Full name <span className="font-normal text-[var(--portfolio-muted)]">(required)</span>
              <input className={fieldClassName} type="text" id="name" name="name" required autoComplete="name" value={formData.name} onChange={handleChange} placeholder="Your full name" />
            </label>
            <label className="text-sm font-semibold text-[var(--portfolio-ink)]" htmlFor="email">
              Email address <span className="font-normal text-[var(--portfolio-muted)]">(required)</span>
              <input className={fieldClassName} type="email" id="email" name="email" required autoComplete="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" />
            </label>
            <label className="text-sm font-semibold text-[var(--portfolio-ink)]" htmlFor="company">
              Company
              <input className={fieldClassName} type="text" id="company" name="company" autoComplete="organization" value={formData.company} onChange={handleChange} placeholder="Company name" />
            </label>
            <label className="text-sm font-semibold text-[var(--portfolio-ink)]" htmlFor="website">
              Website
              <input className={fieldClassName} type="url" id="website" name="website" autoComplete="url" value={formData.website} onChange={handleChange} placeholder="https://example.com" />
            </label>
          </div>

          <label className="mt-5 block text-sm font-semibold text-[var(--portfolio-ink)]" htmlFor="message">
            What do you need help with?
            <textarea className={`${fieldClassName} min-h-36 resize-y`} id="message" name="message" rows={5} value={formData.message} onChange={handleChange} placeholder="Describe the setup, the decision, or the data problem." />
          </label>

          <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex min-h-13 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#00E59E] px-6 py-3.5 font-semibold text-[#1A1D3E] transition hover:bg-[#19F0AD] disabled:cursor-wait disabled:bg-[#C7D0DC] disabled:text-[var(--portfolio-muted)] sm:w-auto"
            >
              {isSubmitting && <SpinnerGap size={20} className="animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Sending request' : 'Send request'}
            </button>
            <p id="contact-response-time" className="text-sm text-[var(--portfolio-muted)]">Reply within 24 hours</p>
          </div>

          <div className="mt-5 min-h-7" aria-live="polite">
            {submitStatus === 'success' && (
              <p className="flex items-center gap-2 text-sm font-medium text-[var(--portfolio-accent-text)]"><CheckCircle size={20} weight="fill" aria-hidden="true" />Thank you. I will be in touch within 24 hours.</p>
            )}
            {submitStatus === 'error' && (
              <p className="flex items-center gap-2 text-sm font-medium text-[#B42318]"><WarningCircle size={20} weight="fill" aria-hidden="true" />Something went wrong. Please try again or email me directly.</p>
            )}
          </div>
        </form>
      </div>
    </section>
  )
}
